import { isDefined } from "@clipboard-health/util-ts";
import { addHours, isAfter, isBefore } from "date-fns";

export interface ValidateClockOutTimestampParams {
  selectedTime: Date;
  clockInTime: Date;
  geofenceDepartureTime?: Date;
  shiftEndAt: Date;
  workerClockOut?: Date;
  isGeofenceFacility?: boolean;
  isNfcOrMeshFacility?: boolean;
  isDirectIntegrationFacility?: boolean;
}

export type ClockOutErrorType = "TIMESTAMPS_NOT_IN_ORDER" | "GEOFENCE_ERROR" | "TOO_LATE_CLOCK_OUT";

export type ClockOutWarningType = "MISSED_PUNCH_WARNING";

export interface ClockOutValidationResult {
  isValid: boolean;
  errorType?: ClockOutErrorType;
  warningType?: ClockOutWarningType;
  message?: string;
}

const TOO_LATE_THRESHOLD_HOURS = 6;

/**
 * Validates clock-out timestamp against business rules
 *
 * Returns error states that block submission or warning states that require
 * a reason but allow submission.
 *
 * Error Priority Order:
 * 1. Timestamps not in order (clock-in after clock-out)
 * 2. Geofence error
 * 3. Too late (>6 hours after shift end)
 *
 * Warning Conditions:
 * - Missed punch warning (blue banner)
 */
export function validateClockOutTimestamp(
  params: ValidateClockOutTimestampParams
): ClockOutValidationResult {
  const {
    selectedTime,
    clockInTime,
    geofenceDepartureTime,
    shiftEndAt,
    workerClockOut,
    isGeofenceFacility = false,
    isNfcOrMeshFacility = false,
    isDirectIntegrationFacility = false,
  } = params;

  // Priority 1: Timestamps not in order
  const timestampsNotInOrderError = checkTimestampsNotInOrderError(selectedTime, clockInTime);
  if (timestampsNotInOrderError) {
    return timestampsNotInOrderError;
  }

  // Priority 2: Geofence error
  const geofenceError = checkGeofenceError(selectedTime, geofenceDepartureTime);
  if (geofenceError) {
    return geofenceError;
  }

  // Priority 3: Too late clock-out
  const tooLateError = checkTooLateError(selectedTime, shiftEndAt);
  if (tooLateError) {
    return tooLateError;
  }

  // Check for missed punch warning (blue warning, not blocking)
  const missedPunchWarning = checkMissedPunchWarning({
    selectedTime,
    workerClockOut,
    isGeofenceFacility,
    isNfcOrMeshFacility,
    isDirectIntegrationFacility,
  });
  if (missedPunchWarning) {
    return missedPunchWarning;
  }

  // No errors or warnings - validation passed
  return {
    isValid: true,
  };
}

/**
 * Error 1: Clock-in time is after selected clock-out time
 */
function checkTimestampsNotInOrderError(
  selectedTime: Date,
  clockInTime: Date
): ClockOutValidationResult | undefined {
  // Clock-out must be after clock-in
  if (isBefore(selectedTime, clockInTime) || selectedTime.getTime() === clockInTime.getTime()) {
    return {
      isValid: false,
      errorType: "TIMESTAMPS_NOT_IN_ORDER",
      message: "Clock-out time must be after clock-in time.",
    };
  }

  return undefined;
}

/**
 * Error 2: Selected time is after geofence departure
 */
function checkGeofenceError(
  selectedTime: Date,
  geofenceDepartureTime?: Date
): ClockOutValidationResult | undefined {
  if (!isDefined(geofenceDepartureTime)) {
    return undefined;
  }

  // Selected time must not be after geofence departure
  if (isAfter(selectedTime, geofenceDepartureTime)) {
    return {
      isValid: false,
      errorType: "GEOFENCE_ERROR",
      message:
        "Clock-out time cannot be after you left the facility. Please select an earlier time.",
    };
  }

  return undefined;
}

/**
 * Error 3: More than 6 hours after scheduled shift end
 */
function checkTooLateError(
  selectedTime: Date,
  shiftEndAt: Date
): ClockOutValidationResult | undefined {
  const tooLateThreshold = addHours(shiftEndAt, TOO_LATE_THRESHOLD_HOURS);

  if (isAfter(selectedTime, tooLateThreshold)) {
    return {
      isValid: false,
      errorType: "TOO_LATE_CLOCK_OUT",
      message: `Clock-out time cannot be more than ${TOO_LATE_THRESHOLD_HOURS} hours after scheduled shift end.`,
    };
  }

  return undefined;
}

/**
 * Warning: Missed punch warning (blue banner)
 *
 * Conditions:
 * - No red error applies AND
 * - At geofence facility: workerClockOut not set OR selected time is later than workerClockOut
 * - At NFC/Mesh/direct integration facility: workerClockOut not set OR selected time is later
 */
function checkMissedPunchWarning(params: {
  selectedTime: Date;
  workerClockOut: Date | undefined;
  isGeofenceFacility: boolean;
  isNfcOrMeshFacility: boolean;
  isDirectIntegrationFacility: boolean;
}): ClockOutValidationResult | undefined {
  const {
    selectedTime,
    workerClockOut,
    isGeofenceFacility,
    isNfcOrMeshFacility,
    isDirectIntegrationFacility,
  } = params;

  // Only applies to facilities with timekeeping systems
  if (!isGeofenceFacility && !isNfcOrMeshFacility && !isDirectIntegrationFacility) {
    return undefined;
  }

  const missedPunchMessage =
    "You are recording a missed punch. No worries - you'll be able to add the reason on the next screen.";

  // Warning if workerClockOut is not set
  if (!isDefined(workerClockOut)) {
    return {
      isValid: true,
      warningType: "MISSED_PUNCH_WARNING",
      message: missedPunchMessage,
    };
  }

  // Warning if selected time is later than workerClockOut
  if (isAfter(selectedTime, workerClockOut)) {
    return {
      isValid: true,
      warningType: "MISSED_PUNCH_WARNING",
      message: missedPunchMessage,
    };
  }

  return undefined;
}
